import { ArrowRight, CalendarClock, ChevronDown, Filter, LayoutGrid, Plus, Rows3, Search, Share2 } from "lucide-react";
import { useEffect, useId, useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { api, formatRelative } from "../api";
import { useAuth } from "../auth";
import type { ProviderId } from "../brands";
import { Button, EmptyState, ErrorState, EvidenceChip, LoadingState, Modal, ProviderMark, Status } from "../components";
import { tagFacets } from "../../shared/tags";
import { type RuleShape, ruleForSharedLoop, visibleTags } from "./sharingBulk";
import { useOwnRuleShares } from "./SharingRules";
import { type LoopDependencySummary, dependencyFacets, needsLine } from "./loopDependencies";
import { providerFacets, providerName } from "./loopPlacements";
import {
  LOOP_FILTERS,
  type LedgerCriteria,
  type LoopFilter,
  type LoopsView,
  evidenceFor,
  facetSuffix,
  filterLoops,
  hasFacetFilter,
  lastSignalCell,
  markProviderFor,
  originFor,
  presentedStatus,
  readLoopsView,
  reliabilityCell,
  rowTitle,
  runsOnCell,
  secondLine,
  segmentCounts,
  statusCell,
  writeLoopsView,
} from "./loopLedger";
import "../styles/cockpit.css";

// The loop predicates live in loopLedger.ts; LoopDetailPage and older callers still import them from here.
export { evidenceFor, hasFailedLatest, homeLineFor, isLive, markProviderFor, originFor, presentedStatus, type LoopFilter, type PresentedStatus } from "./loopLedger";

type Discovery = {
  source: { id: string; provider: string | null; name: string | null };
  externalId: string | null;
  kind: string | null;
  confidence: string | null;
  firstDiscoveredAt: string | null;
  lastCatalogedAt: string | null;
};

export type LoopModel = {
  id: string;
  externalId: string | null;
  name: string;
  description: string | null;
  cadence: string;
  scheduleHint: string | null;
  status: string;
  tags: string[];
  team: { id: string; name: string } | null;
  owner: { id: string; name: string } | null;
  /** rule names the sharing rule that granted a shared loop to this group; absent or null for a hand grant. */
  visibility: { mode: "native" | "shared"; originOrganizationId: string; originOrganizationName: string; originOrganizationKind: string; readOnly: boolean; rule?: RuleShape | null; ruleId?: string | null };
  discovery: Discovery | null;
  performance: { runs: number; successes: number; failures: number; resolvedRuns: number; successRate: number | null; outcomeCoverage: number | null; averageDurationMs: number | null; lastRunAt: string | null; lastRunStatus: string | null; lastRunProvider: string | null };
  runsOn?: { provider: string; agentName: string | null; basis: "placement" | "observed" } | null;
  /** A count of what the loop needs and up to three required names; absent until the worker reports dependencies. */
  dependencies?: LoopDependencySummary | null;
};

export type Team = { id: string; name: string };
export type Finding = { id: string; severity: string; title: string; detail: string; loopId?: string };
export type SharingOverview = { groups: Array<{ id: string; name: string }>; loops: Array<{ id: string; sharedTo: string[] }> };
export type RunModel = { id: string; externalId: string; status: string; agentName: string | null; agentProvider: string | null; startedAt: string; completedAt: string | null; durationMs: number | null; inputTokens: number | null; outputTokens: number | null; costUsd: number | null; outcomeSummary: string | null; errorMessage: string | null };

export function LoopsPage() {
  const { activeOrganization, personalOrganization } = useAuth();
  const organizationId = activeOrganization?.id;
  const inGroup = activeOrganization?.kind === "group";
  const [fetched, setFetched] = useState<LoopModel[]>([]);
  // In a group, the viewer's own rule-made shares get their rule from the personal workspace; the loop list does not carry it.
  const ownRules = useOwnRuleShares(personalOrganization?.id, inGroup);
  const loops = useMemo(() => !inGroup || !organizationId ? fetched : fetched.map((loop) => {
    if (loop.visibility.mode !== "shared" || loop.visibility.rule) return loop;
    const rule = ruleForSharedLoop(loop, organizationId, ownRules);
    return rule ? { ...loop, visibility: { ...loop.visibility, rule } } : loop;
  }), [fetched, inGroup, organizationId, ownRules]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<LoopFilter>("all");
  const [dependency, setDependency] = useState<string | null>(null);
  const [tag, setTag] = useState<string | null>(null);
  const [provider, setProvider] = useState<ProviderId | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [view, setView] = useState<LoopsView>(() => readLoopsView(localStorage));
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const filtersId = useId();

  useEffect(() => {
    if (!organizationId) return;
    const controller = new AbortController();
    setLoading(true);
    setError("");
    void Promise.all([
      api<{ loops: LoopModel[] }>("/api/app/loops", { signal: controller.signal }, organizationId),
      api<{ teams: Team[] }>("/api/app/group", { signal: controller.signal }, organizationId),
    ]).then(([loopData, groupData]) => {
      setFetched(loopData.loops);
      setTeams(groupData.teams);
    }).catch((caught: unknown) => {
      if (caught instanceof DOMException && caught.name === "AbortError") return;
      setError(caught instanceof Error ? caught.message : "The loop registry is unavailable.");
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [organizationId, refreshKey]);

  const criteria = useMemo<LedgerCriteria>(() => ({ query, filter, dependency, tag, provider }), [query, filter, dependency, tag, provider]);
  const counts = useMemo(() => segmentCounts(loops), [loops]);
  const filtered = useMemo(() => filterLoops(loops, criteria), [loops, criteria]);
  const facets = useMemo(() => providerFacets(loops), [loops]);
  const needsFacets = useMemo(() => dependencyFacets(loops), [loops]);
  const tagFacetList = useMemo(() => tagFacets(loops, 8), [loops]);
  const hasFacets = facets.length > 0 || needsFacets.length > 0 || tagFacetList.length > 0;
  // An active facet keeps the disclosure open so the chip that narrows the view stays in reach.
  const disclosureOpen = hasFacets && (filtersOpen || hasFacetFilter(criteria));

  function chooseView(next: LoopsView) {
    setView(next);
    writeLoopsView(localStorage, next);
  }

  function clearFilters() {
    setQuery("");
    setFilter("all");
    setDependency(null);
    setTag(null);
    setProvider(null);
    setFiltersOpen(false);
  }

  const narrowed = [provider && `runs on ${providerName(provider)}`, dependency && `needs "${dependency}"`, tag && `the tag "${tag}"`].filter(Boolean);

  return (
    <div className="page cockpit-page loops-page">
      <header className="page-header cockpit-page-header">
        <div><p className="eyebrow">Cataloged and observed</p><h1>Loops</h1><p>Read operational state, evidence quality, and origin without treating catalog presence as execution.</p></div>
        <Button onClick={() => setOpen(true)}><Plus size={15} aria-hidden="true" /> Declare a loop</Button>
      </header>

      {loading ? (
        <LoadingState>Mapping the visible loop registry…</LoadingState>
      ) : error ? (
        <ErrorState action={<Button onClick={() => setRefreshKey((value) => value + 1)}>Try again</Button>}>{error}</ErrorState>
      ) : loops.length === 0 ? (
        <EmptyState eyebrow="No loops yet" title="Start with what your agents already run" action={<div className="empty-state__actions"><Link className="button button--primary" to="/app/onboarding">Scan an agent <ArrowRight size={15} aria-hidden="true" /></Link><Button variant="secondary" onClick={() => setOpen(true)}><Plus size={15} aria-hidden="true" /> Declare by hand</Button></div>}>
          One scan-only prompt maps the loops an agent can substantiate. You can also declare a loop yourself; declaring one does not prove a run occurred.
        </EmptyState>
      ) : (
        <>
          <div className="loop-ledger-toolbar">
            <div className="loop-segments" role="group" aria-label="Loop states">
              {LOOP_FILTERS.map((option) => (
                <button key={option.value} type="button" className={filter === option.value ? "is-selected" : ""} aria-pressed={filter === option.value} title={option.description} onClick={() => setFilter(option.value)}>
                  <span>{option.label}</span><b>{counts[option.value]}</b>
                </button>
              ))}
            </div>
            <label className="cockpit-search loop-ledger-toolbar__search"><Search size={15} aria-hidden="true" /><span className="sr-only">Search loops</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search loops, sources, needs, or tags" /></label>
            <div className="loop-ledger-toolbar__tools">
              {hasFacets ? (
                <button type="button" className="loop-ledger-toolbar__filters" aria-expanded={disclosureOpen} aria-controls={filtersId} onClick={() => setFiltersOpen((value) => !value || hasFacetFilter(criteria))}>
                  <Filter size={14} aria-hidden="true" /><span>Filters{facetSuffix(criteria)}</span><ChevronDown size={14} aria-hidden="true" className={disclosureOpen ? "is-open" : ""} />
                </button>
              ) : null}
              <span className="loop-ledger-toolbar__count"><b>{filtered.length}</b> shown</span>
              <div className="loop-view-toggle" role="group" aria-label="List view">
                <button type="button" aria-pressed={view === "ledger"} onClick={() => chooseView("ledger")}><Rows3 size={14} aria-hidden="true" /> Ledger</button>
                <button type="button" aria-pressed={view === "cards"} onClick={() => chooseView("cards")}><LayoutGrid size={14} aria-hidden="true" /> Cards</button>
              </div>
            </div>
          </div>

          {disclosureOpen ? (
            <div id={filtersId} className="loop-ledger-filters">
              {facets.length > 0 ? (
                <div className="loop-ledger-filters__line" role="group" aria-label="Loops by the agent they run on">
                  <span className="loop-ledger-filters__label">Runs on</span>
                  {facets.map((facet) => {
                    const selected = provider === facet.provider;
                    return <button key={facet.provider} type="button" className="loop-chip" aria-pressed={selected} onClick={() => setProvider(selected ? null : facet.provider)}><ProviderMark provider={facet.provider} size={14} />{providerName(facet.provider)}<b>{facet.count}</b></button>;
                  })}
                </div>
              ) : null}
              {needsFacets.length > 0 ? (
                <div className="loop-ledger-filters__line" role="group" aria-label="Loops by what they need">
                  <span className="loop-ledger-filters__label">Needs</span>
                  {needsFacets.map((facet) => {
                    const selected = dependency?.toLowerCase() === facet.name.toLowerCase();
                    return <button key={facet.name} type="button" className="loop-chip" aria-pressed={selected} onClick={() => setDependency(selected ? null : facet.name)}>{facet.name}<b>{facet.count}</b></button>;
                  })}
                </div>
              ) : null}
              {tagFacetList.length > 0 ? (
                <div className="loop-ledger-filters__line" role="group" aria-label="Loops by tag">
                  <span className="loop-ledger-filters__label">Tags</span>
                  {tagFacetList.map((facet) => {
                    const selected = tag === facet.tag;
                    return <button key={facet.tag} type="button" className="loop-chip" aria-pressed={selected} onClick={() => setTag(selected ? null : facet.tag)}>{facet.tag}<b>{facet.count}</b></button>;
                  })}
                </div>
              ) : null}
            </div>
          ) : null}

          {filtered.length === 0 ? (
            <EmptyState eyebrow="No match" title="Widen the current view" action={<Button variant="secondary" onClick={clearFilters}>Clear filters</Button>}>
              {narrowed.length ? `No loop matches the current search, evidence-state filter, and ${narrowed.join(" and ")}.` : "No loop matches the current search and evidence-state filter."}
            </EmptyState>
          ) : view === "cards" ? (
            <section className="cockpit-loop-grid" aria-label="Filtered loops">
              {filtered.map((loop) => {
                const evidence = evidenceFor(loop);
                const evaluable = loop.performance.resolvedRuns >= 3 && loop.performance.successRate != null;
                const needs = needsLine(loop.dependencies);
                const tags = visibleTags(loop.tags);
                return (
                  <Link to={`/app/loops/${loop.id}`} className={`cockpit-loop-card ${loop.visibility.mode === "shared" ? "cockpit-loop-card--shared" : ""}`} key={loop.id}>
                    <header><ProviderMark provider={markProviderFor(loop)} size={40} /><Status value={presentedStatus(loop.performance.lastRunStatus)} /></header>
                    <div className="cockpit-loop-card__identity"><p className="eyebrow">{loop.visibility.mode === "shared" ? <><Share2 size={10} aria-hidden="true" /> {originFor(loop)}</> : originFor(loop)}</p><h2>{loop.name}</h2><p>{loop.description || "No description supplied."}</p></div>
                    {tags.shown.length > 0 ? <p className="loop-tags" aria-label="Tags">{tags.shown.map((entry) => <span key={entry}>{entry}</span>)}{tags.more > 0 ? <span className="is-more">+{tags.more}</span> : null}</p> : null}
                    <div className="cockpit-loop-card__evidence"><EvidenceChip stage={evidence.stage}>{evidence.label}</EvidenceChip><small>{evidence.detail}</small></div>
                    <dl>
                      <div><dt>Run history</dt><dd>{loop.performance.runs}</dd></div>
                      <div><dt>Reliability</dt><dd>{evaluable ? `${Math.round(loop.performance.successRate! * 100)}%` : "—"}</dd><small>{evaluable ? `${loop.performance.resolvedRuns} resolved` : "needs 3 resolved"}</small></div>
                      <div><dt>Outcome coverage</dt><dd>{loop.performance.outcomeCoverage == null ? "—" : `${Math.round(loop.performance.outcomeCoverage * 100)}%`}</dd><small>{loop.performance.runs ? `${loop.performance.resolvedRuns} resolved` : "no run history"}</small></div>
                    </dl>
                    <footer><span><CalendarClock size={14} aria-hidden="true" /> {loop.cadence}</span><span>{loop.performance.lastRunAt ? `Last signal ${formatRelative(loop.performance.lastRunAt)}` : "No runs reported yet"} <ArrowRight size={14} aria-hidden="true" /></span></footer>
                    {needs ? <p className="loop-needs" title={needs}>{needs}</p> : null}
                  </Link>
                );
              })}
            </section>
          ) : (
            <section className="loop-ledger" aria-label="Filtered loops">
              <div className="loop-ledger__cols" aria-hidden="true"><span>Loop</span><span>Cadence</span><span>Runs on</span><span>Evidence</span><span>Last signal</span><span /></div>
              {filtered.map((loop) => {
                const evidence = evidenceFor(loop);
                const reliability = reliabilityCell(loop.performance);
                const runsOn = runsOnCell(loop);
                const signal = lastSignalCell(loop.performance.lastRunAt);
                const status = statusCell(loop);
                const tags = visibleTags(loop.tags);
                const shared = loop.visibility.mode === "shared";
                return (
                  <Link to={`/app/loops/${loop.id}`} className={`loop-ledger__row${shared ? " is-shared" : ""}`} key={loop.id} title={rowTitle(loop)}>
                    <span className="loop-ledger__name">
                      <ProviderMark provider={markProviderFor(loop)} size={26} />
                      <span className="loop-ledger__identity">
                        <strong>{loop.name}</strong>
                        {tags.shown.length > 0 ? <span className="loop-tags" aria-label="Tags">{tags.shown.map((entry) => <span key={entry}>{entry}</span>)}{tags.more > 0 ? <span className="is-more">+{tags.more}</span> : null}</span> : null}
                        {shared ? <small className="loop-ledger__origin"><Share2 size={10} aria-hidden="true" />{originFor(loop)}</small> : null}
                        <small className="loop-ledger__meta">{secondLine(loop)}</small>
                      </span>
                    </span>
                    <span className="loop-ledger__cadence">{loop.cadence}</span>
                    <span className="loop-ledger__runs-on">{runsOn.text}</span>
                    <span className="loop-ledger__evidence">
                      <EvidenceChip stage={evidence.stage}>{evidence.label}</EvidenceChip>
                      <span className={`loop-ledger__reliability${reliability.withheld ? " is-withheld" : ""}`}><b>{reliability.value}</b><small>{reliability.detail}</small></span>
                    </span>
                    <span className="loop-ledger__signal" title={signal.title}>{signal.text}</span>
                    <span className={`loop-ledger__status status status--${status.tone}`} title={status.label}><span className="status__dot" /><span className="sr-only">{status.label}</span></span>
                  </Link>
                );
              })}
            </section>
          )}
        </>
      )}

      <LoopFormModal open={open} teams={teams} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); setRefreshKey((value) => value + 1); }} />
    </div>
  );
}

export function LoopFormModal({ open, teams, loop, onClose, onSaved }: { open: boolean; teams: Team[]; loop?: LoopModel; onClose: () => void; onSaved: () => void }) {
  const { activeOrganization } = useAuth();
  const [name, setName] = useState(loop?.name ?? "");
  const [description, setDescription] = useState(loop?.description ?? "");
  const [cadence, setCadence] = useState(loop?.cadence ?? "daily");
  const [scheduleHint, setScheduleHint] = useState(loop?.scheduleHint ?? "");
  const [teamId, setTeamId] = useState(loop?.team?.id ?? "");
  const [tags, setTags] = useState(loop?.tags.join(", ") ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setName(loop?.name ?? "");
    setDescription(loop?.description ?? "");
    setCadence(loop?.cadence ?? "daily");
    setScheduleHint(loop?.scheduleHint ?? "");
    setTeamId(loop?.team?.id ?? "");
    setTags(loop?.tags.join(", ") ?? "");
    setError("");
  }, [open, loop]);

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!activeOrganization) return;
    setBusy(true);
    setError("");
    try {
      await api(`/api/app/loops${loop ? `/${loop.id}` : ""}`, { method: loop ? "PATCH" : "POST", body: JSON.stringify({ name, description: description || undefined, cadence, scheduleHint: scheduleHint || undefined, teamId: teamId || null, tags: tags.split(",").map((tag) => tag.trim()).filter(Boolean) }) }, activeOrganization.id);
      onSaved();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The loop could not be saved");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} title={loop ? "Edit loop" : "Declare a loop"} onClose={onClose}>
      <form className="form-stack" onSubmit={save}>
        <label><span>Name</span><input value={name} onChange={(event) => setName(event.target.value)} placeholder="Customer signal review" required minLength={2} /></label>
        <label><span>Description</span><textarea value={description} onChange={(event) => setDescription(event.target.value)} placeholder="What outcome does this loop pursue?" rows={3} /></label>
        <div className="form-row"><label><span>Cadence</span><select value={cadence} onChange={(event) => setCadence(event.target.value)}><option>continuous</option><option>hourly</option><option>daily</option><option>weekly</option><option>event-driven</option><option>manual</option><option>custom</option></select></label><label><span>Team</span><select value={teamId} onChange={(event) => setTeamId(event.target.value)}><option value="">Unassigned</option>{teams.map((team) => <option key={team.id} value={team.id}>{team.name}</option>)}</select></label></div>
        <label><span>Schedule note</span><input value={scheduleHint} onChange={(event) => setScheduleHint(event.target.value)} placeholder="Weekdays at 08:00 CT" /></label>
        <label><span>Tags</span><input value={tags} onChange={(event) => setTags(event.target.value)} placeholder="research, customer, weekly" /></label>
        {error ? <div className="form-error" role="alert">{error}</div> : null}
        <div className="modal__actions"><Button type="button" variant="ghost" onClick={onClose}>Cancel</Button><Button type="submit" busy={busy}>{loop ? "Save changes" : "Declare loop"}</Button></div>
      </form>
    </Modal>
  );
}
